import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Typography,
  Form,
  Input,
  Button,
  Space,
  Alert,
  Modal,
  Skeleton,
  Tooltip,
  Divider,
  Tag
} from 'antd';
import {
  MailOutlined,
  SaveOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  DisconnectOutlined,
  GoogleOutlined,
  UserOutlined,
  EditOutlined,
  SendOutlined
} from '@ant-design/icons';
import {
  getGmailSmtp,
  saveGmailCredentials,
  getGmailAuthUrl,
  handleGmailCallback,
  disconnectGmail,
  deleteGmailSmtp,
  sendTestEmail,
  clearMessages
} from '../../features/settings/settingsSlice';

const { Title, Text } = Typography;

const GmailSmtpSettings = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [showSecret, setShowSecret] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const { gmailSmtp, smtpLoading, connectLoading, testEmailLoading, error, success } = useSelector(
    (state) => state.settings
  );

  const hasCredentials = gmailSmtp?.clientId && gmailSmtp?.redirectUri;
  const isConnected = gmailSmtp?.isConnected;

  // Check for OAuth callback in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const errorParam = urlParams.get('error');

    if (code && state) {
      // Handle OAuth callback
      dispatch(handleGmailCallback({ code, state })).then(() => {
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      });
    } else if (errorParam) {
      // Handle OAuth error
      console.error('OAuth error:', errorParam);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(getGmailSmtp());
  }, [dispatch]);

  useEffect(() => {
    if (gmailSmtp?.clientId) {
      form.setFieldsValue({
        clientId: gmailSmtp.clientId,
        clientSecret: gmailSmtp.clientSecret,
        redirectUri: gmailSmtp.redirectUri
      });
    }
  }, [gmailSmtp, form]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(clearMessages());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  const handleSaveCredentials = async (values) => {
    const payload = {
      clientId: values.clientId,
      clientSecret: values.clientSecret !== '••••••••' ? values.clientSecret : undefined,
      redirectUri: values.redirectUri
    };

    await dispatch(saveGmailCredentials(payload));
    setIsEditing(false);
  };

  const handleConnect = async () => {
    const result = await dispatch(getGmailAuthUrl());
    if (result.payload?.authUrl) {
      // Redirect to Google OAuth
      window.location.href = result.payload.authUrl;
    }
  };

  const handleDisconnect = () => {
    Modal.confirm({
      title: 'Disconnect Gmail',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to disconnect your Gmail account?</p>
          <p className="text-gray-500 text-sm mt-2">
            Connected as: <strong>{gmailSmtp?.connectedEmail}</strong>
          </p>
        </div>
      ),
      okText: 'Yes, Disconnect',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => dispatch(disconnectGmail())
    });
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Gmail SMTP Settings',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete all Gmail SMTP settings? This will disconnect your account and remove all credentials.',
      okText: 'Yes, Delete All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        await dispatch(deleteGmailSmtp());
        form.resetFields();
        setIsEditing(false);
      }
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
    form.setFieldValue('clientSecret', '');
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (gmailSmtp?.clientId) {
      form.setFieldsValue({
        clientId: gmailSmtp.clientId,
        clientSecret: gmailSmtp.clientSecret,
        redirectUri: gmailSmtp.redirectUri
      });
    } else {
      form.resetFields();
    }
  };

  if (smtpLoading && !gmailSmtp) {
    return (
      <Card className="shadow-soft border border-border-light rounded-xl">
        <Skeleton active />
      </Card>
    );
  }

  return (
    <Card className="shadow-soft border border-border-light rounded-xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center">
              <GoogleOutlined className="text-lg text-white" />
            </div>
            <div>
              <Title level={3} className="mb-0">
                Gmail SMTP / OAuth
              </Title>
              <Text type="secondary" className="text-sm">
                Connect your Google account for email sending
              </Text>
            </div>
          </div>
          {isConnected ? (
            <Tag color="success" icon={<CheckCircleOutlined />}>
              Connected
            </Tag>
          ) : hasCredentials ? (
            <Tag color="warning">Not Connected</Tag>
          ) : (
            <Tag color="default">Not Configured</Tag>
          )}
        </div>

        {/* Connection Status Card */}
        {isConnected && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <UserOutlined className="text-green-600" />
                </div>
                <div>
                  <Text strong className="block">Connected as</Text>
                  <Text className="text-green-700">{gmailSmtp?.connectedEmail}</Text>
                </div>
              </div>
              <Button
                danger
                icon={<DisconnectOutlined />}
                onClick={handleDisconnect}
                loading={connectLoading}
              >
                Remove Connection
              </Button>
            </div>
          </div>
        )}

        {/* Test Email Section */}
        {isConnected && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <Text strong className="block mb-3">Send Test Email</Text>
            <div className="flex gap-2">
              <Input
                placeholder="Enter email address"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                type="email"
                className="flex-1"
                prefix={<MailOutlined className="text-gray-400" />}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => {
                  if (testEmail) {
                    dispatch(sendTestEmail(testEmail));
                  }
                }}
                loading={testEmailLoading}
                disabled={!testEmail}
              >
                Send Test
              </Button>
            </div>
            <Text type="secondary" className="text-xs mt-2 block">
              Send a test email to verify your Gmail connection is working
            </Text>
          </div>
        )}

        {/* Connect Button - Show when credentials exist but not connected */}
        {hasCredentials && !isConnected && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <Text strong className="block">Ready to Connect</Text>
                <Text type="secondary" className="text-sm">
                  Your credentials are saved. Click to authorize with Google.
                </Text>
              </div>
              <Button
                type="primary"
                icon={<LinkOutlined />}
                onClick={handleConnect}
                loading={connectLoading}
                size="large"
              >
                Connect to Google
              </Button>
            </div>
          </div>
        )}

        {success && (
          <Alert message={success} type="success" showIcon closable />
        )}

        {error && (
          <Alert message={error} type="error" showIcon closable />
        )}

        <Divider />

        {/* Credentials Form */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <Title level={5} className="mb-0">OAuth 2.0 Credentials</Title>
            {hasCredentials && !isEditing && (
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={handleEdit}
              >
                Edit Credentials
              </Button>
            )}
          </div>

          <Alert
            message="Google Cloud Console Setup"
            description={
              <span>
                Create OAuth 2.0 credentials in the{' '}
                <a
                  href="https://console.cloud.google.com/apis/credentials"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Cloud Console
                </a>
                . Make sure to enable the Gmail API and add your redirect URI.
              </span>
            }
            type="info"
            showIcon
            className="mb-4"
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSaveCredentials}
            disabled={hasCredentials && !isEditing}
          >
            <Form.Item
              label="Client ID"
              name="clientId"
              rules={[{ required: true, message: 'Please enter your Client ID' }]}
            >
              <Input
                placeholder="Enter Google OAuth Client ID"
                className="font-mono text-sm"
              />
            </Form.Item>

            <Form.Item
              label="Client Secret"
              name="clientSecret"
              rules={[
                {
                  required: !hasCredentials || isEditing,
                  message: 'Please enter your Client Secret'
                }
              ]}
              extra="Your client secret will be encrypted before storage"
            >
              <Input.Password
                placeholder={hasCredentials && !isEditing ? '••••••••' : 'Enter Google OAuth Client Secret'}
                className="font-mono text-sm"
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
                visibilityToggle={{
                  visible: showSecret,
                  onVisibleChange: setShowSecret
                }}
              />
            </Form.Item>

            <Form.Item
              label="Authorized Redirect URI"
              name="redirectUri"
              rules={[
                { required: true, message: 'Please enter your Redirect URI' },
                { type: 'url', message: 'Please enter a valid URL' }
              ]}
              extra="Add this exact URL to your Google Cloud Console OAuth settings"
            >
              <Input
                placeholder="https://yourdomain.com/settings"
                className="font-mono text-sm"
                addonBefore={<LinkOutlined />}
              />
            </Form.Item>

            {isEditing && (
              <Alert
                message="Updating credentials"
                description="Leave the Client Secret field empty to keep the existing secret, or enter a new one to update it."
                type="warning"
                showIcon
                className="mb-4"
              />
            )}

            <div className="flex justify-between">
              <div>
                {hasCredentials && (
                  <Tooltip title="Delete all Gmail settings">
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDelete}
                      loading={smtpLoading}
                    >
                      Delete All Settings
                    </Button>
                  </Tooltip>
                )}
              </div>
              <Space>
                {isEditing && (
                  <Button onClick={handleCancel}>Cancel</Button>
                )}
                {(!hasCredentials || isEditing) && (
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={smtpLoading}
                  >
                    {hasCredentials ? 'Update Credentials' : 'Save Credentials'}
                  </Button>
                )}
              </Space>
            </div>
          </Form>
        </div>

        {/* Instructions */}
        <Divider />
        <div className="bg-gray-50 p-4 rounded-lg">
          <Text strong className="block mb-3">Setup Instructions:</Text>
          <ol className="list-decimal list-inside space-y-2 text-gray-600 text-sm">
            <li>Go to the <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600">Google Cloud Console</a></li>
            <li>Create a new project or select an existing one</li>
            <li>Enable the <strong>Gmail API</strong> from the API Library</li>
            <li>Go to <strong>Credentials</strong> &gt; <strong>Create Credentials</strong> &gt; <strong>OAuth 2.0 Client ID</strong></li>
            <li>Select <strong>Web application</strong> as the application type</li>
            <li>Add your <strong>Authorized redirect URI</strong> (the URL where users will be redirected after authorization)</li>
            <li>Copy the <strong>Client ID</strong> and <strong>Client Secret</strong></li>
            <li>Paste them here and click <strong>Save Credentials</strong></li>
            <li>Click <strong>Connect to Google</strong> to authorize your account</li>
          </ol>
        </div>
      </div>
    </Card>
  );
};

export default GmailSmtpSettings;
